import { Trans, useLingui } from "@lingui/react/macro";
import { useEffect } from "react";
import {
  ANY_TIME_OF_DAY,
  rescheduleActivity,
  type IRescheduleActivity,
} from "@/api/Activities";
import { FilledButton } from "@medoma/components/Button/Button";
import Form from "@/components/Form/Form";
import { FormProvider, useForm } from "react-hook-form";
import {
  RecurrencePicker,
  type IRecurrencePickerFields,
} from "@/forms/AddActivity/Scheduling/RecurrencePicker";
import {
  customRecurrenceIntervalUnitSchema,
  recurringNumberDictionary,
  recurringOptionSchema,
} from "@/forms/AddActivity/Scheduling/schedulingModels";
import {
  TimeSlotsPicker,
  type ITimeSlotsPickerFields,
} from "@/forms/AddActivity/Scheduling/TimeSlotsPicker";
import {
  recurrencesPerDayOptionSchema,
  timespanToHours,
} from "@/forms/AddActivity/Scheduling/timeSlotsUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deducedError } from "@/Utils/ErrorUtils";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  WeekdaysPicker,
  type IWeekdaysPickerFields,
} from "@/forms/AddActivity/Scheduling/WeekdaysPicker";
import {
  getTimeFields,
  getTimespan,
} from "@/forms/AddActivity/activityTimeUtils";
import { timeOfDaySchema } from "@medoma/models/activities";
import * as Sentry from "@sentry/react";
import {
  CustomRecurringInterval,
  type ICustomRecurringIntervalFields,
} from "@/forms/AddActivity/Scheduling/CustomRecurringInput";
import type { ITimeSensitivityFields } from "@/forms/AddActivity/Scheduling/TimeSensitivityPicker";
import type { IRecurringActivitySchedule, ITime } from "@/api/Activities";

type IEditScheduleFields = IRecurrencePickerFields &
  IWeekdaysPickerFields &
  ICustomRecurringIntervalFields &
  ITimeSlotsPickerFields &
  ITimeSensitivityFields;

const intervalAsWeeksOrDays = (interval: number) => {
  if (interval % 7 === 0 && interval >= 7) {
    return {
      number: interval / 7,
      unit: customRecurrenceIntervalUnitSchema.enum.weeks,
    };
  }
  return {
    number: interval,
    unit: customRecurrenceIntervalUnitSchema.enum.days,
  };
};

export const EditActivitySchedule = ({
  activityId,
  schedule,
  onSuccess,
}: {
  activityId: string;
  schedule: IRecurringActivitySchedule;
  onSuccess: () => void;
}) => {
  const { t } = useLingui();
  const scheduleTypeIsInterval = "interval" in schedule;
  const scheduleTypeIsWeekdays = "days" in schedule;

  const times = schedule.times;
  const isAnyTimeOfDay = times === ANY_TIME_OF_DAY;
  const hasTimeslots = !isAnyTimeOfDay && times.length > 0;
  const timeSensitivity = hasTimeslots
    ? timespanToHours(schedule.span)
    : undefined;

  const methods = useForm<IEditScheduleFields>({
    defaultValues: {
      recurrence: scheduleTypeIsWeekdays
        ? recurringOptionSchema.enum.onSelectedWeekdays
        : scheduleTypeIsInterval
          ? (recurringNumberDictionary[schedule.interval] ??
            recurringOptionSchema.enum.custom)
          : null,
      customRecurrenceInterval: scheduleTypeIsInterval
        ? intervalAsWeeksOrDays(schedule.interval).number.toString()
        : null,
      customRecurrenceIntervalUnit: scheduleTypeIsInterval
        ? intervalAsWeeksOrDays(schedule.interval).unit
        : null,
      timeCategory: isAnyTimeOfDay
        ? timeOfDaySchema.enum.Any
        : timeOfDaySchema.enum.Specific,
      recurrencesPerDay: hasTimeslots
        ? recurrencesPerDayOptionSchema.parse(times.length.toString())
        : "1",
      timeslots: hasTimeslots ? times.map((time: ITime) => ({ time })) : [],
      timeSensitivity,
      weekdays: scheduleTypeIsWeekdays ? schedule.days : [],
    },
  });

  const {
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    reset,
    setError,
    unregister,
    watch,
  } = methods;

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (formData: IRescheduleActivity) =>
      rescheduleActivity(activityId, formData),
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess();
    },
  });

  const validateAndSubmit = handleSubmit((validatedFormData) => {
    const {
      recurrence,
      timeCategory,
      timeSensitivity,
      timeslots,
      weekdays,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit,
    } = validatedFormData;

    if (recurrence === null) {
      Sentry.captureException("Failed to edit schedule. Recurrence is null.");
      return;
    }

    const isAnyTimeOfDay = timeCategory === timeOfDaySchema.enum.Any;
    const isRecurringAsInterval =
      recurrence !== recurringOptionSchema.enum.onSelectedWeekdays;

    const recurringTimeOfDayFields = isAnyTimeOfDay
      ? {
          isAnyTimeOfDay: true as const,
          timeslots: undefined,
        }
      : {
          isAnyTimeOfDay: false as const,
          timeslots: timeslots as { time: string }[],
        };

    const rescheduleActivityFields: IRescheduleActivity = {
      span: getTimespan({ isAnyTimeOfDay, timeSensitivity }),
      ...getTimeFields({
        isRecurringAsInterval,
        weekdays,
        recurrence,
        customRecurrenceInterval,
        customRecurrenceIntervalUnit,
        ...recurringTimeOfDayFields,
      }),
    };

    mutate(rescheduleActivityFields);
  });

  // oxlint-disable-next-line react/incompatible-library
  const recurrence = watch("recurrence");
  const isRecurringOnWeekdays =
    recurrence === recurringOptionSchema.enum.onSelectedWeekdays;
  const isCustomRecurring = recurrence === recurringOptionSchema.enum.custom;

  useEffect(() => {
    if (!isRecurringOnWeekdays) {
      unregister("weekdays");
    }
  }, [isRecurringOnWeekdays, unregister]);

  useEffect(() => {
    if (!isCustomRecurring) {
      unregister("customRecurrenceInterval");
      unregister("customRecurrenceIntervalUnit");
    }
  }, [isCustomRecurring, unregister]);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {isPending ? (
          <Loading message={t`Ändrar schemat`} />
        ) : errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} />
        ) : null}
        <RecurrencePicker />
        {isRecurringOnWeekdays ? <WeekdaysPicker /> : null}
        {isCustomRecurring ? <CustomRecurringInterval /> : null}
        <TimeSlotsPicker isRecurring />
        {isDirty ? (
          <FilledButton type="submit">
            <Trans>Spara ändringar</Trans>
          </FilledButton>
        ) : null}
      </Form>
    </FormProvider>
  );
};
